import { useState, useCallback, useRef, useEffect } from 'react';
import { env } from '@/lib/env';
import { ModuleSearchBar } from '@/common/components/ModuleSearchBar';
import { MobileSearchToggle } from '@/common/components/MobileSearchToggle';
import { ScrollableNav } from '@/common/components/ScrollableNav';
import { useIsCompactLayout } from '@/common/hooks';
import type { BriefingResponse, NewsStory, NewsPanelProps } from './types';
import { getHeaders } from './lib/api';
import { formatPrice, timeAgo, formatStoryDate } from './utils/format';
import { CATEGORY_ICONS, NEWS_TABS, DEFAULT_ICON } from './constants';
import '@/modules/_infrastructure/ModuleStyles.css';
import './NewsPanel.css';

export function NewsPanel({ config }: NewsPanelProps) {
  const isMobile = useIsCompactLayout();
  const [briefing, setBriefing] = useState<BriefingResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [instructions, setInstructions] = useState(
    config.instructions || 'Top news across politics, markets, tech, and world events'
  );
  const [activeTab, setActiveTab] = useState('headlines');
  const mountedRef = useRef(true);

  // Per-tab cache — switch tabs instantly, fetch in background if stale
  const tabCacheRef = useRef<Map<string, BriefingResponse>>(new Map());
  const STALE_MS = 5 * 60 * 1000; // 5 minutes

  const tickers = (config.tickers || 'SPX,NDX,GC,BTC,ETH').split(',').map(t => t.trim());

  // Core fetch function — silent=true means no loading spinners (background refresh)
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  const doFetch = useCallback(async (fetchInstructions: string, tabId: string, silent: boolean) => {
    if (!silent) {
      if (tabCacheRef.current.has(tabId)) setIsRefreshing(true); else setIsLoading(true);
    }
    setError(null);
    try {
      const res = await fetch(`${env.apiBaseUrl}/news/briefing`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ instructions: fetchInstructions, tickers }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(data.error || `Failed: ${res.status}`);
      }
      const data: BriefingResponse = await res.json();
      if (mountedRef.current) {
        tabCacheRef.current.set(tabId, data);
        // Only update displayed briefing if still on the same tab
        if (activeTabRef.current === tabId) setBriefing(data);
      }
    } catch (err: any) {
      if (mountedRef.current && !silent) setError(err.message || 'Failed to load news briefing');
    } finally {
      if (mountedRef.current) { setIsLoading(false); setIsRefreshing(false); }
    }
  }, [tickers.join(',')]);

  const doFetchRef = useRef(doFetch);
  doFetchRef.current = doFetch;

  // On mount: load DB cache, then fetch fresh headlines
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const res = await fetch(`${env.apiBaseUrl}/news/briefing?instructions=${encodeURIComponent(NEWS_TABS[0].instructions)}`, { method: 'GET', headers: getHeaders() });
        if (res.ok) {
          const data: BriefingResponse = await res.json();
          if (data.stories?.length > 0 && mountedRef.current) {
            setBriefing(data);
            tabCacheRef.current.set('headlines', data);
          }
        }
      } catch { /* silent */ }
      // Fetch fresh in background
      doFetchRef.current(NEWS_TABS[0].instructions, 'headlines', true);
    })();
    return () => { mountedRef.current = false; };
  }, []);

  // Try loading from server DB cache (fast GET)
  const loadServerCache = useCallback(async (tabId: string, tabInstructions: string): Promise<boolean> => {
    try {
      const res = await fetch(
        `${env.apiBaseUrl}/news/briefing?instructions=${encodeURIComponent(tabInstructions)}`,
        { method: 'GET', headers: getHeaders() }
      );
      if (res.ok) {
        const data: BriefingResponse = await res.json();
        if (data.stories?.length > 0 && mountedRef.current) {
          tabCacheRef.current.set(tabId, data);
          if (activeTabRef.current === tabId) setBriefing(data);
          return true;
        }
      }
    } catch { /* silent */ }
    return false;
  }, []);

  // Switch tab: client cache → server DB cache → full fetch
  const switchTab = useCallback(async (tabId: string, tabInstructions: string) => {
    setActiveTab(tabId);
    setInstructions(tabInstructions);

    // 1. Client cache — instant
    const cached = tabCacheRef.current.get(tabId);
    if (cached) {
      setBriefing(cached);
      const age = Date.now() - new Date(cached.generatedAt).getTime();
      if (age > STALE_MS) doFetchRef.current(tabInstructions, tabId, true);
      return;
    }

    // 2. Server DB cache — fast (~50ms)
    setIsLoading(true);
    const fromDb = await loadServerCache(tabId, tabInstructions);
    if (fromDb) {
      setIsLoading(false);
      // Background refresh if stale
      const dbCached = tabCacheRef.current.get(tabId);
      if (dbCached) {
        const age = Date.now() - new Date(dbCached.generatedAt).getTime();
        if (age > STALE_MS) doFetchRef.current(tabInstructions, tabId, true);
      }
      return;
    }

    // 3. No cache anywhere — full fetch with spinner
    setIsLoading(false);
    setBriefing(null);
    doFetchRef.current(tabInstructions, tabId, false);
  }, [loadServerCache]);

  // Manual refresh button — always fetches fresh with loading indicator
  const handleRefresh = useCallback(() => {
    doFetchRef.current(instructions, activeTab, false);
  }, [instructions, activeTab]);


  // When searching, search across all stories (current + any from other tabs cached in allStories)
  const [allStories, setAllStories] = useState<NewsStory[]>([]);

  // Accumulate stories from each briefing load
  useEffect(() => {
    if (briefing?.stories) {
      setAllStories(prev => {
        const existing = new Map(prev.map(s => [s.headline, s]));
        for (const s of briefing.stories) existing.set(s.headline, s);
        return Array.from(existing.values());
      });
    }
  }, [briefing?.stories]);

  const filteredStories = (() => {
    if (!searchQuery.trim()) return briefing?.stories || [];
    const q = searchQuery.toLowerCase();
    return allStories.filter(s => s.headline.toLowerCase().includes(q) || s.body.toLowerCase().includes(q));
  })();

  return (
    <div className="module module-news">
      {/* Topic tabs — matches Pages module pattern */}
      <div className="module-nav">
        <div className="module-nav__primary">
          <ScrollableNav>
            {NEWS_TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`module-toolbar-button${activeTab === tab.id ? ' is-active' : ''}`}
                onClick={() => switchTab(tab.id, tab.instructions)}
              >
                <span className="module-toolbar-label" data-text={tab.label}>{tab.label}</span>
              </button>
            ))}
          </ScrollableNav>
        </div>
        <div className="module-nav__secondary">
          <MobileSearchToggle targetId="news-search-toolbar" />
          <button
            type="button"
            className="mobile-action-button"
            onClick={handleRefresh}
            disabled={isLoading || isRefreshing}
            title="Refresh"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Search (hidden on mobile until magnifying glass tapped) */}
      <div id="news-search-toolbar" className="module-toolbar module-search-row">
        <ModuleSearchBar
          standalone={false}
          value={searchQuery}
          onChange={setSearchQuery}
          onClose={() => setSearchQuery('')}
          placeholder="Search stories..."
          autoFocus={false}
        />
        {!isMobile && (
          <button className="module-toolbar-button is-primary" onClick={handleRefresh} disabled={isLoading || isRefreshing}>
            {isRefreshing ? 'Updating...' : '↻ Refresh'}
          </button>
        )}
      </div>

      {/* Ticker bar */}
      {briefing?.tickers && briefing.tickers.length > 0 && (
        <div className="news-ticker-bar">
          {briefing.tickers.map(t => (
            <span key={t.symbol} className="news-ticker-item">
              <span className="news-ticker-label">{t.label || t.symbol}</span>
              <span className="news-ticker-price">${formatPrice(t.price)}</span>
              <span className={`news-ticker-change ${t.change >= 0 ? 'positive' : 'negative'}`}>
                {t.change >= 0 ? '▲' : '▼'}{Math.abs(t.change).toFixed(1)}%
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="module-content">
        {isLoading && !briefing && (
          <div className="news-loading">
            <div className="news-loading-spinner" />
            <span>Searching for news...</span>
          </div>
        )}

        {error && !isLoading && !briefing && (
          <div className="news-error">
            <span>{error}</span>
            <button onClick={handleRefresh} className="news-retry-btn">Try again</button>
          </div>
        )}

        {briefing && (
          <>
            <div className="news-updated">
              Updated {timeAgo(briefing.generatedAt)} · {filteredStories.length} stories
              {isRefreshing && <span className="news-refreshing-indicator"> · Updating...</span>}
            </div>
            <div className="news-stories">
              {filteredStories.map((story, i) => {
                const cat = story.category?.toLowerCase() || '';
                const iconCfg = CATEGORY_ICONS[cat] || DEFAULT_ICON;
                return (
                  <div key={i} className="news-card">
                    <div className="news-card-icon" style={{ background: iconCfg.bg, color: iconCfg.color }}>
                      {iconCfg.icon}
                    </div>
                    <div className="news-card-content">
                      <div className="news-card-headline">{story.headline}</div>
                      <div className="news-card-body">{story.body}</div>
                      <div className="news-card-tags">
                        {story.category && <span className="news-tag">{story.category.charAt(0).toUpperCase() + story.category.slice(1)}</span>}
                        {story.sources.map((s, j) => (
                          <a key={j} href={s.url} target="_blank" rel="noopener noreferrer" className="news-tag news-tag-source">
                            {s.name}
                          </a>
                        ))}
                      </div>
                    </div>
                    <div className="news-card-meta">
                      <span className="news-card-date">{formatStoryDate(briefing.generatedAt)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {filteredStories.length === 0 && searchQuery && (
              <div className="news-no-results">No stories match &ldquo;{searchQuery}&rdquo;</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
